import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Link2Off } from "lucide-react";
import { cn } from "@/lib/utils";
import { usd } from "@/lib/format";
import {
  LINK_TYPE_DIRECT,
  LINK_TYPE_GOOGLE_RESULT,
  LINK_TYPE_GOOGLE_SEARCH,
  buySourceNavigation,
  hasPreviewToken,
  isValidProductImageSrc,
  normalizeBuySource,
} from "./normalizeSource";
import { merchantIconFor, normalizeMerchantDomain } from "./merchantIcon";
import { fetchMerchantIconUrl } from "@/lib/api";

const LINK_TOOLTIP = {
  [LINK_TYPE_DIRECT]: "Open product",
  [LINK_TYPE_GOOGLE_RESULT]: "Open source via Google",
  [LINK_TYPE_GOOGLE_SEARCH]: "Search this product on Google",
};
const NO_LINK_TOOLTIP = "No source link available";

/** Magnifier crop size relative to the thumbnail (CSS background-size). */
export const BUY_SOURCE_MAGNIFIER_ZOOM = 2.4;

function money(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "—";
  return usd(v);
}

function formatDiff(diff) {
  if (typeof diff !== "number" || Number.isNaN(diff)) return null;
  if (diff < 0) return `${usd(Math.abs(diff))} below Walmart`;
  if (diff > 0) return `+${usd(diff)} vs Walmart`;
  return null;
}

function canHoverFinePointer() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

function cssUrl(src) {
  // Safe for https and data: URLs — escape quotes/backslashes for CSS url("…").
  return `url("${String(src).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}")`;
}

/**
 * Pointer-controlled magnified crop of the product image (portal).
 * Title overlays the bottom of the same preview surface — no separate tooltip bubble.
 */
function BuySourceImagePreview({
  anchorRef,
  src,
  open,
  title,
  focal = { x: 50, y: 50 },
}) {
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || !open) {
      setCoords(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const pad = 8;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const previewW = Math.min(320, Math.max(280, Math.round(vw * 0.22)));
    const previewH = Math.round(previewW * 1.05);
    let left = rect.left + rect.width / 2 - previewW / 2;
    left = Math.min(Math.max(left, pad), window.innerWidth - pad - previewW);
    let top = rect.top - previewH - 12;
    if (top < pad) {
      top = Math.min(rect.bottom + 12, window.innerHeight - pad - previewH);
    }
    setCoords({ left, top, width: previewW, height: previewH });
  }, [anchorRef, open]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, src, focal.x, focal.y]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  if (!open || !src || !coords || typeof document === "undefined") return null;

  const bgSize = `${BUY_SOURCE_MAGNIFIER_ZOOM * 100}%`;
  const bgPos = `${focal.x}% ${focal.y}%`;
  const showTitle = Boolean(title && String(title).trim());

  return createPortal(
    <div
      className="buy-source-image-preview"
      data-testid="buy-source-image-preview"
      data-magnifier="true"
      data-zoom={String(BUY_SOURCE_MAGNIFIER_ZOOM)}
      aria-hidden
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        width: coords.width,
        height: coords.height,
        pointerEvents: "none",
        zIndex: 10040,
      }}
    >
      <div
        className="buy-source-image-preview__zoom"
        data-testid="buy-source-image-preview-zoom"
        style={{
          backgroundImage: cssUrl(src),
          backgroundSize: bgSize,
          backgroundPosition: bgPos,
          backgroundRepeat: "no-repeat",
        }}
      />
      {showTitle ? (
        <div
          className="buy-source-image-preview__title"
          data-testid="buy-source-image-preview-title"
        >
          {title}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

/**
 * Compact full-title tip for cards WITHOUT a product image.
 * Not the old white speech-bubble — dark floating label above the card.
 * Image cards use the magnifier title overlay instead.
 */
function BuySourceNoImageTitleTip({ anchorRef, title, open }) {
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || !open) {
      setCoords(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const pad = 8;
    const maxWidth = Math.min(320, Math.max(rect.width, 200));
    let left = rect.left + rect.width / 2;
    left = Math.min(
      Math.max(left, pad + maxWidth / 2),
      window.innerWidth - pad - maxWidth / 2,
    );
    const top = Math.max(pad, rect.top - 8);
    setCoords({
      left,
      top,
      width: maxWidth,
      transform: "translate(-50%, -100%)",
    });
  }, [anchorRef, open]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, title]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  if (!open || !title || !coords || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="buy-source-noimage-title-tip"
      data-testid="buy-source-noimage-title-tip"
      role="tooltip"
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        width: coords.width,
        maxWidth: "min(320px, calc(100vw - 16px))",
        transform: coords.transform,
        pointerEvents: "none",
        zIndex: 10050,
      }}
    >
      <div className="buy-source-noimage-title-tip__text">{title}</div>
    </div>,
    document.body,
  );
}

/**
 * Compact Buy Source card.
 * Clickable when the row has its own navigable URL (merchant PDP, else its
 * Google offer/product page). Native <a href> opens a new tab; the icon is
 * visual only inside the same anchor.
 */
export function BuySourceCard({
  source,
  onPreview,
  walmartTargetPrice,
  showEconomics = false,
  hideVerificationBadge = false,
  className,
}) {
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [noImageTitleOpen, setNoImageTitleOpen] = useState(false);
  const [focal, setFocal] = useState({ x: 50, y: 50 });
  const cardRef = useRef(null);
  const thumbRef = useRef(null);
  const normalized = normalizeBuySource(source) || source || {};
  const domain = normalized?.source_domain || normalized?.source_name || "source";
  const title =
    normalized?.candidate_title ||
    normalized?.title ||
    normalized?.product_title ||
    domain;
  const hasCandidateTitle = Boolean(title && title !== domain);
  const badge = normalized?.verification_badge || normalized?.verification_status;
  const canPreview = hasPreviewToken(normalized);
  const imageSrc = isValidProductImageSrc(normalized?.image_url) ? normalized.image_url : null;

  // Local badge only — never an external favicon request.
  const [merchantImageFailed, setMerchantImageFailed] = useState(false);
  const [resolvedIconUrl, setResolvedIconUrl] = useState(null);
  const merchantIcon = merchantIconFor(domain, normalized?.source_name);

  // Resolution order: Ezisell first-party merchant-icon resolver → initials.
  // Provider-supplied favicon URLs are NEVER injected into <img> (that would
  // be a browser third-party fetch); the domain is normalized through Ezisell.
  useEffect(() => {
    let cancelled = false;
    setMerchantImageFailed(false);
    setResolvedIconUrl(null);
    const host = normalizeMerchantDomain(domain);
    if (!host) {
      setMerchantImageFailed(true);
      return () => { cancelled = true; };
    }
    fetchMerchantIconUrl(host).then((url) => {
      if (cancelled) return;
      if (url) {
        setResolvedIconUrl(url);
      } else {
        setResolvedIconUrl(null);
        setMerchantImageFailed(true);
      }
    });
    return () => { cancelled = true; };
  }, [domain]);

  const observed = normalized?.observed_purchase_price;
  const equivalent = normalized?.target_equivalent_cost ?? normalized?.display_cost;
  const displayPrice =
    typeof equivalent === "number"
      ? equivalent
      : typeof observed === "number"
        ? observed
        : typeof normalized?.price === "number"
          ? normalized.price
          : null;
  const diff =
    normalized?.difference_vs_walmart ??
    (walmartTargetPrice != null && equivalent != null
      ? Math.round((equivalent - walmartTargetPrice) * 100) / 100
      : null);

  const updateFocalFromEvent = (event) => {
    const el = thumbRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setFocal({
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    });
  };

  const showImagePreview = (event) => {
    if (!canHoverFinePointer() || !imageSrc) return;
    if (event) updateFocalFromEvent(event);
    setImagePreviewOpen(true);
  };
  const moveImagePreview = (event) => {
    if (!imagePreviewOpen) return;
    updateFocalFromEvent(event);
  };
  const hideImagePreview = () => setImagePreviewOpen(false);

  const showNoImageTitle = ({ force = false } = {}) => {
    if (imageSrc || !hasCandidateTitle) return;
    // Hover tip needs fine pointer; keyboard focus may open without hover media.
    if (!force && !canHoverFinePointer()) return;
    setNoImageTitleOpen(true);
  };
  const hideNoImageTitle = () => setNoImageTitleOpen(false);

  const handlePreview = () => {
    // PDP preview overlay is opt-in for non-navigable cards only.
    // Never auto-open a full-screen overlay on trusted clickable cards — it
    // intercepts clicks (fixed inset-0) and blocks merchant navigation.
    if (canPreview) onPreview?.(normalized);
  };

  const navigation = buySourceNavigation(normalized);
  const externalHref = navigation.href;
  const linkType = navigation.type;
  const clickable = Boolean(externalHref);
  const merchantLabel = normalized?.source_name || domain;
  const openLabel = {
    [LINK_TYPE_GOOGLE_RESULT]: `Open ${merchantLabel} source via Google`,
    [LINK_TYPE_GOOGLE_SEARCH]: `Search ${merchantLabel} for this product on Google`,
  }[linkType] || `Open ${merchantLabel} product`;

  const body = (
    <>
      <div
        className={`buy-source-favicon${resolvedIconUrl && !merchantImageFailed ? "" : " buy-source-favicon--local"}`}
        data-testid="buy-source-favicon"
        data-merchant-icon={merchantIcon.kind}
      >
        {resolvedIconUrl && !merchantImageFailed ? (
          <img
            src={resolvedIconUrl}
            alt=""
            aria-hidden
            width={20}
            height={20}
            draggable={false}
            onError={() => setMerchantImageFailed(true)}
          />
        ) : (
          <span
            className="buy-source-initials"
            aria-hidden
            style={merchantIcon.color ? { background: merchantIcon.color } : undefined}
          >
            {merchantIcon.text}
          </span>
        )}
      </div>

      <div className="buy-source-copy min-w-0 flex-1">
        <div
          className="buy-source-domain truncate"
          data-testid="buy-source-domain"
          title={!imageSrc && hasCandidateTitle ? title : undefined}
        >
          {domain}
          {hasCandidateTitle ? (
            <span className="buy-source-title-inline" data-testid="buy-source-title-inline">
              {" "}
              / {title}
            </span>
          ) : null}
        </div>
        <div className="buy-source-price-row">
          {showEconomics ? (
            <>
              {observed != null ? (
                <span className="buy-source-meta" title="Observed source price">
                  Obs {money(observed)}
                </span>
              ) : null}
              <span className="buy-source-price" data-testid="buy-source-price">
                {money(equivalent ?? displayPrice)}
              </span>
            </>
          ) : (
            <span className="buy-source-price" data-testid="buy-source-price">
              {money(displayPrice)}
            </span>
          )}
          {!hideVerificationBadge && badge ? (
            <span className="buy-source-badge">{badge}</span>
          ) : null}
          {showEconomics && formatDiff(diff) ? (
            <span className="buy-source-diff">{formatDiff(diff)}</span>
          ) : null}
          {!showEconomics && !hideVerificationBadge && formatDiff(diff) ? (
            <span className="buy-source-diff">{formatDiff(diff)}</span>
          ) : null}
        </div>
      </div>

      {imageSrc ? (
        <div
          ref={thumbRef}
          className="buy-source-thumb"
          data-testid="buy-source-product-image"
          onMouseEnter={showImagePreview}
          onMouseMove={moveImagePreview}
          onMouseLeave={hideImagePreview}
          onFocus={showImagePreview}
          onBlur={hideImagePreview}
        >
          <img
            src={imageSrc}
            alt=""
            draggable={false}
            data-testid="buy-source-image-loupe"
            className="buy-source-thumb__img"
          />
        </div>
      ) : null}

      {clickable ? (
        <span
          className="buy-source-external"
          aria-hidden
          data-testid="buy-source-external-link"
          data-href={externalHref}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </span>
      ) : (
        <span
          className="buy-source-no-link"
          data-testid="buy-source-no-link"
          title={NO_LINK_TOOLTIP}
          aria-label={NO_LINK_TOOLTIP}
          role="img"
        >
          <Link2Off className="h-3 w-3" aria-hidden />
        </span>
      )}
    </>
  );

  const sharedProps = {
    ref: cardRef,
    className: cn(
      "buy-source-card-approved buy-source-card-compact shrink-0",
      clickable ? "buy-source-card--clickable" : "buy-source-card--static",
      className,
    ),
    onMouseEnter: () => {
      // Never open the blocking SourcePreviewOverlay on hover for clickable
      // merchant cards — that overlay covers the viewport and steals clicks.
      // Non-clickable cards may still open the PDP preview on hover.
      if (!clickable) handlePreview();
      showNoImageTitle();
    },
    onMouseLeave: () => {
      hideImagePreview();
      hideNoImageTitle();
    },
    onFocus: () => {
      if (!clickable) handlePreview();
      showNoImageTitle({ force: true });
    },
    onBlur: () => {
      hideNoImageTitle();
    },
    "data-testid": "buy-source-card",
    "data-preview-enabled": canPreview ? "true" : "false",
    "data-source-domain": domain,
    "data-clickable": clickable ? "true" : "false",
    "data-link-type": linkType,
    "data-has-image": imageSrc ? "true" : "false",
    "data-direct-url": linkType === LINK_TYPE_DIRECT ? externalHref : undefined,
    "data-candidate-title": hasCandidateTitle ? title : undefined,
  };

  const overlays = (
    <>
      <BuySourceImagePreview
        anchorRef={thumbRef}
        src={imageSrc}
        open={imagePreviewOpen}
        title={hasCandidateTitle ? title : ""}
        focal={focal}
      />
      {!imageSrc ? (
        <BuySourceNoImageTitleTip
          anchorRef={cardRef}
          title={hasCandidateTitle ? title : ""}
          open={noImageTitleOpen}
        />
      ) : null}
    </>
  );

  if (clickable) {
    return (
      <>
        <a
          {...sharedProps}
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={openLabel}
          title={LINK_TOOLTIP[linkType]}
        >
          {body}
        </a>
        {overlays}
      </>
    );
  }

  return (
    <>
      <div {...sharedProps} tabIndex={hasCandidateTitle && !imageSrc ? 0 : undefined}>
        {body}
      </div>
      {overlays}
    </>
  );
}
